// src/lib/imageUrls.ts
// Deterministic CDN URL helpers for profile/feed/chat images.
// - If value starts with http(s), return as-is.
// - Otherwise, insert suffix ("_thumb"/"_avatar") before extension and prefix the area CDN base.
// - Fallback to a default person image when key is missing for profile/avatar helpers.

type Area = 'profile' | 'feed' | 'chat';

// Single canonical CDN base for all media areas
const CDN_HOST = (process.env.NEXT_PUBLIC_CDN_HOSTNAME || '').replace(/\/$/, '');
const CDN_BASE_ENV = (process.env.NEXT_PUBLIC_CDN_BASE || '').replace(/\/$/, '');
const CDN_BASE_SINGLE = CDN_BASE_ENV
  ? CDN_BASE_ENV
  : (CDN_HOST ? `https://${CDN_HOST}` : '');

const FALLBACK_PERSON = '/img/person.svg';

function isAbsolute(u?: string | null): boolean {
  return !!u && /^https?:\/\//i.test(u);
}

function baseFor(_area: Area): string {
  return CDN_BASE_SINGLE;
}

function stripQuery(s: string): string {
  const idx = s.indexOf('?');
  return idx >= 0 ? s.slice(0, idx) : s;
}

function ensureLeadingNoSlash(s: string): string {
  return s.replace(/^\/+/, '');
}

function insertSuffixBeforeExt(path: string, suffix: '_thumb' | '_avatar'): string {
  const clean = stripQuery(path);
  // Guard against double suffix
  if (new RegExp(`${suffix}(\n|$|\.)`).test(clean)) return clean;
  const lastSlash = clean.lastIndexOf('/');
  const dir = lastSlash >= 0 ? clean.slice(0, lastSlash + 1) : '';
  const file = lastSlash >= 0 ? clean.slice(lastSlash + 1) : clean;
  const dot = file.lastIndexOf('.');
  if (dot <= 0) {
    // No extension; append suffix
    return `${dir}${file}${suffix}`;
  }
  const name = file.slice(0, dot);
  const ext = file.slice(dot); // includes dot
  return `${dir}${name}${suffix}${ext}`;
}

export function toCdn(keyOrUrl?: string | null, area: Area = 'profile'): string {
  if (!keyOrUrl || !String(keyOrUrl).trim()) return '';
  const v = String(keyOrUrl).trim();
  if (isAbsolute(v)) return v;
  const base = baseFor(area);
  const cleaned = ensureLeadingNoSlash(stripQuery(v));
  return base ? `${base}/${cleaned}` : `/${cleaned}`;
}

export function profileThumb(key?: string | null): string {
  if (!key || !String(key).trim()) return FALLBACK_PERSON;
  const v = String(key).trim();
  if (isAbsolute(v)) return v;
  let cleaned = ensureLeadingNoSlash(stripQuery(v));
  // Convert _avatar to _thumb for better display quality on talent cards.
  cleaned = cleaned.replace(/_avatar(\.[a-z0-9]+)$/i, '_thumb$1');
  return toCdn(cleaned, 'profile') || FALLBACK_PERSON;
}

export function profileAvatar(key?: string | null): string {
  if (!key || !String(key).trim()) return FALLBACK_PERSON;
  const v = String(key).trim();
  if (isAbsolute(v)) return v;
  const withSuffix = insertSuffixBeforeExt(v, '_avatar');
  return toCdn(withSuffix, 'profile') || FALLBACK_PERSON;
}

export function feedThumb(key?: string | null): string {
  if (!key || !String(key).trim()) return FALLBACK_PERSON;
  const v = String(key).trim();
  if (isAbsolute(v)) return v;
  const withSuffix = insertSuffixBeforeExt(v, '_thumb');
  return toCdn(withSuffix, 'feed') || FALLBACK_PERSON;
}

export function chatThumb(key?: string | null): string {
  if (!key || !String(key).trim()) return FALLBACK_PERSON;
  const v = String(key).trim();
  if (isAbsolute(v)) return v;
  const withSuffix = insertSuffixBeforeExt(v, '_thumb');
  return toCdn(withSuffix, 'chat') || FALLBACK_PERSON;
}

export default {
  toCdn,
  profileThumb,
  profileAvatar,
  feedThumb,
  chatThumb,
};
